import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { and, eq, isUndefined, useLiveQuery } from "@tanstack/react-db";
import { memo, useMemo } from "react";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";
import {
  NewConversationModal,
  type ExistingDraftData,
} from "./new-conversation-modal";

/**
 * Renders the NewConversationModal pre-filled with data from an existing DRAFT conversation.
 * Used by ConversationView when the conversation status is "DRAFT".
 */
export const DraftConversationModal = memo(function DraftConversationModal({
  conversation,
  thread,
  onClose,
  onCreated,
}: {
  conversation: ConversationListItemResponse;
  thread: ConversationThread;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const collections = useGlobalConversationCollections();
  const draftResult = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          and(
            eq(draft.conversationId, String(conversation.id)),
            eq(draft.threadId, String(thread.id)),
            isUndefined(draft.inReplyToMessageId),
          ),
        )
        .findOne(),
    [conversation.id, thread.id],
  );
  const threadDraft = draftResult.data ?? null;

  const subject =
    thread.integrationType === "gmail" || thread.integrationType === "outlook"
      ? (thread.channelData?.subject ?? undefined)
      : undefined;

  const existingDraft = useMemo<ExistingDraftData>(
    () => ({
      conversationId: conversation.id,
      threadId: thread.id,
      integrationId: thread.integrationId,
      customerEmail: conversation.customer?.email ?? undefined,
      cc: threadDraft?.cc?.map((r) => r.email).join(", ") || undefined,
      bcc: threadDraft?.bcc?.map((r) => r.email).join(", ") || undefined,
      subject: subject ?? undefined,
      draftContent: threadDraft?.content ?? undefined,
    }),
    [
      conversation.id,
      conversation.customer?.email,
      thread.id,
      thread.integrationId,
      subject,
      threadDraft?.content,
      threadDraft?.cc,
      threadDraft?.bcc,
    ],
  );

  return (
    <NewConversationModal
      existingDraft={existingDraft}
      onCreated={onCreated}
      open
      setOpen={(open) => {
        if (!open) onClose();
      }}
    />
  );
});
